import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Phone, Globe, Clock, Camera, 
  UtensilsCrossed, ChevronRight, ChevronLeft, Check,
  Upload, Plus, Trash2, DollarSign, Star, Loader2,
  Search, Lightbulb, AlertCircle, Crop, X, FileText,
  Sparkles, Info, Image as ImageIcon, Gift, Calendar
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import Navigation from '@/components/Navigation';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import Cropper from 'react-easy-crop';

// ===== CONSTANTS =====
const STEPS = [
  { id: 1, title: 'Business Info', icon: Building2 },
  { id: 2, title: 'Neighborhood', icon: MapPin },
  { id: 3, title: 'Location & Contact', icon: Phone },
  { id: 4, title: 'Hours & Details', icon: Clock },
  { id: 5, title: 'Photos & Logo', icon: Camera },
  { id: 6, title: 'Menu', icon: UtensilsCrossed },
  { id: 7, title: 'Reservations', icon: Calendar },
  { id: 8, title: 'Experiences', icon: Gift },
  { id: 9, title: 'Review & Submit', icon: Check }
];

const NEIGHBORHOODS = [
  { id: 'rino', name: 'RiNo (River North)', description: 'Arts district with breweries & galleries' },
  { id: 'cherry-creek', name: 'Cherry Creek', description: 'Upscale shopping & fine dining' },
  { id: 'lodo', name: 'LoDo (Lower Downtown)', description: 'Central nightlife hub near Union Station' },
  { id: 'sobo', name: 'South Broadway (SoBo)', description: 'Eclectic & retro vibes' },
  { id: 'highland-lohi', name: 'Highland / LoHi', description: 'Trendy spots with downtown views' },
  { id: 'capitol-hill', name: 'Capitol Hill / Uptown', description: 'Historic & artistic nightlife' },
  { id: 'tennyson', name: 'Tennyson Street (Berkeley)', description: 'Artsy & cozy neighborhood' },
  { id: 'other', name: 'Other Neighborhood', description: 'Not listed above? Enter your neighborhood' }
];

const VENUE_TYPES = [
  'Bar & Restaurant', 'Brewery', 'Wine Bar', 'Cocktail Lounge', 'Event Space',
  'Art Gallery', 'Music Venue', 'Rooftop Bar', 'Sports Bar', 'Coffee Shop', 'Other'
];

const DEFAULT_AMENITIES = [
  'Outdoor Seating', 'Private Rooms', 'Live Music', 'Happy Hour', 'Full Bar',
  'Craft Beer', 'Wine Selection', 'Cocktail Menu', 'Food Menu', 'Late Night',
  'Pet Friendly', 'Wheelchair Accessible', 'Free WiFi', 'Parking Available',
  'Valet Parking', 'TV/Sports', 'Dance Floor', 'DJ', 'Karaoke', 'Games',
  'Brunch', 'Takeout', 'Delivery', 'Private Dining', 'Rooftop', 'Fireplace',
  'Pool Table', 'Arcade', 'Photo Booth'
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const HOUR_PRESETS = [
  { label: 'Restaurant (11am-10pm)', open: '11:00', close: '22:00' },
  { label: 'Bar (4pm-2am)', open: '16:00', close: '02:00' },
  { label: 'Brunch Spot (8am-3pm)', open: '08:00', close: '15:00' },
  { label: 'All Day (9am-11pm)', open: '09:00', close: '23:00' },
  { label: 'Late Night (6pm-2am)', open: '18:00', close: '02:00' },
];

const DESC_GUIDELINES = {
  min: 150,
  ideal: 300,
  max: 800,
  tips: [
    'Start with what makes your venue unique (your hook)',
    'Describe the atmosphere and vibe guests can expect',
    'Mention signature offerings (drinks, food, events)',
    'Include what sets you apart from other venues',
    'End with why eventgoers should visit',
  ]
};

const GOOGLE_MAPS_KEY = 'AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g';

// ===== HELPER: Load Google Maps API =====
const loadGoogleMaps = () => {
  return new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return; }
    if (document.getElementById('google-maps-script')) {
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve(); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve(); }
      }, 100);
    };
    document.head.appendChild(script);
  });
};

// ===== HELPER: Google Place Search Component =====
const GooglePlaceSearch = ({ onPlaceSelected }) => {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const serviceRef = useRef(null);
  const detailsRef = useRef(null);

  useEffect(() => {
    loadGoogleMaps().then(() => {
      setMapsLoaded(true);
      serviceRef.current = new window.google.maps.places.AutocompleteService();
      detailsRef.current = new window.google.maps.places.PlacesService(document.createElement('div'));
    }).catch(() => { /* Google Maps not available */ });
  }, []);

  const searchPlaces = useCallback((input) => {
    if (!serviceRef.current || input.length < 3) { setResults([]); return; }
    setSearching(true);
    serviceRef.current.getPlacePredictions(
      { input, types: ['establishment'], componentRestrictions: { country: 'us' } },
      (predictions, status) => {
        setSearching(false);
        if (status === 'OK' && predictions) {
          setResults(predictions.slice(0, 5));
        } else {
          setResults([]);
        }
      }
    );
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => { if (query) searchPlaces(query); }, 300);
    return () => clearTimeout(timeout);
  }, [query, searchPlaces]);

  // Parse Google hours like "Monday: 11:00 AM – 10:00 PM" to our format
  const parseGoogleHours = (weekdayText) => {
    const dayMap = { 'monday': 0, 'tuesday': 1, 'wednesday': 2, 'thursday': 3, 'friday': 4, 'saturday': 5, 'sunday': 6 };
    const result = DAYS.map(day => ({ day: day.toLowerCase(), open_time: '11:00', close_time: '23:00', is_closed: false }));
    
    for (const line of weekdayText) {
      const match = line.match(/^(\w+):\s*(.+)$/);
      if (!match) continue;
      const dayName = match[1].toLowerCase();
      const hours = match[2].trim();
      const idx = dayMap[dayName];
      if (idx === undefined) continue;
      
      if (hours.toLowerCase() === 'closed') {
        result[idx].is_closed = true;
      } else {
        // Parse "11:00 AM – 10:00 PM" format
        const timeParts = hours.split('–').map(s => s.trim());
        if (timeParts.length === 2) {
          result[idx].open_time = convertTo24(timeParts[0]);
          result[idx].close_time = convertTo24(timeParts[1]);
        }
      }
    }
    return result;
  };

  const convertTo24 = (timeStr) => {
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return '12:00';
    let h = parseInt(match[1]);
    const m = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${m}`;
  };

  const selectPlace = (placeId) => {
    if (!detailsRef.current) return;
    detailsRef.current.getDetails(
      { placeId, fields: ['name', 'formatted_address', 'address_components', 'formatted_phone_number', 'website', 'opening_hours', 'geometry', 'photos', 'types', 'editorial_summary', 'reviews'] },
      (place, status) => {
        if (status === 'OK' && place) {
          const getComponent = (type) => place.address_components?.find(c => c.types.includes(type))?.long_name || '';
          const getShort = (type) => place.address_components?.find(c => c.types.includes(type))?.short_name || '';
          
          const streetNumber = getComponent('street_number');
          const route = getComponent('route');
          const neighborhood = getComponent('neighborhood') || getComponent('sublocality_level_1') || getComponent('sublocality');
          
          // Map Google place types to our venue types
          const typeMap = {
            'restaurant': 'Bar & Restaurant', 'bar': 'Bar & Restaurant', 'night_club': 'Cocktail Lounge',
            'cafe': 'Coffee Shop', 'brewery': 'Brewery', 'art_gallery': 'Art Gallery',
            'event_venue': 'Event Space', 'lodging': 'Event Space', 'meal_delivery': 'Bar & Restaurant',
          };
          const googleTypes = place.types || [];
          let venueType = '';
          for (const gt of googleTypes) {
            if (typeMap[gt]) { venueType = typeMap[gt]; break; }
          }
          
          // Map neighborhood to our neighborhood IDs
          const neighborhoodMap = {
            'river north': 'rino', 'rino': 'rino', 'five points': 'rino',
            'cherry creek': 'cherry-creek',
            'lodo': 'lodo', 'lower downtown': 'lodo', 'union station': 'lodo',
            'south broadway': 'sobo', 'baker': 'sobo',
            'highland': 'highland-lohi', 'lohi': 'highland-lohi', 'lower highlands': 'highland-lohi',
            'capitol hill': 'capitol-hill', 'uptown': 'capitol-hill',
            'tennyson': 'tennyson', 'berkeley': 'tennyson',
          };
          let matchedNeighborhood = '';
          const lowerNeighborhood = neighborhood.toLowerCase();
          for (const [key, value] of Object.entries(neighborhoodMap)) {
            if (lowerNeighborhood.includes(key)) { matchedNeighborhood = value; break; }
          }
          
          // Build a description from available data
          let description = '';
          if (place.editorial_summary?.text) {
            description = place.editorial_summary.text;
          } else if (place.reviews?.length > 0) {
            // Use a snippet from the top review as a starting point
            const topReview = place.reviews.sort((a, b) => b.rating - a.rating)[0];
            if (topReview?.text) {
              description = `${place.name} is a popular ${venueType || 'venue'} in ${neighborhood || 'Denver'}. `;
            }
          }
          if (!description && place.name) {
            description = `${place.name} is a ${venueType || 'venue'} located in ${neighborhood || getComponent('locality') || 'Denver'}, Colorado. `;
          }
          
          onPlaceSelected({
            business_name: place.name || '',
            address: `${streetNumber} ${route}`.trim(),
            city: getComponent('locality') || getComponent('sublocality'),
            state: getShort('administrative_area_level_1'),
            zip_code: getComponent('postal_code'),
            phone: place.formatted_phone_number || '',
            website: place.website || '',
            venue_type: venueType,
            neighborhood: matchedNeighborhood,
            neighborhood_name: neighborhood,
            description: description,
            // Parse business hours from Google
            business_hours: place.opening_hours?.weekday_text ? parseGoogleHours(place.opening_hours.weekday_text) : null,
            // Get photo URLs
            photos: place.photos ? place.photos.slice(0, 6).map(p => p.getUrl({ maxWidth: 1200 })) : [],
          });
          
          setQuery('');
          setResults([]);
          toast.success(`Imported details from ${place.name}`);
        }
      }
    );
  };

  return (
    <div className="relative" data-testid="google-place-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder="Search for your business on Google..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10"
          data-testid="google-search-input"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((result) => (
            <button
              key={result.place_id}
              onClick={() => selectPlace(result.place_id)}
              className="w-full text-left px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 border-b last:border-0 transition-colors"
              data-testid="google-place-result"
            >
              <p className="font-medium text-sm text-foreground">{result.structured_formatting?.main_text}</p>
              <p className="text-xs text-muted-foreground">{result.structured_formatting?.secondary_text}</p>
            </button>
          ))}
        </div>
      )}
      {!mapsLoaded && <p className="text-xs text-muted-foreground mt-1">Loading Google Places...</p>}
    </div>
  );
};

// ===== HELPER: Image Crop Dialog =====
const createImage = (url) => new Promise((resolve, reject) => {
  const image = new window.Image();
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', (error) => reject(error));
  image.crossOrigin = 'anonymous';
  image.src = url;
});

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}

const ImageCropDialog = ({ open, onClose, imageSrc, aspectRatio, onCropComplete, title }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onCropDone = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('file', blob, 'cropped.jpg');
      const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onCropComplete(res.data.url);
      onClose();
    } catch (e) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (!imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title || 'Crop Image'}</DialogTitle>
        </DialogHeader>
        <div className="relative w-full h-80 bg-black rounded-lg overflow-hidden">
          <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={aspectRatio || 16 / 9}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropDone} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Zoom</span>
          <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={uploading}>
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading...</> : <><Crop className="w-4 h-4 mr-2" /> Crop & Save</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== HELPER: Photo Upload Button =====
const PhotoUploadButton = ({ onImageSelected, label, className }) => {
  const inputRef = useRef(null);
  return (
    <>
      <button onClick={() => inputRef.current?.click()} className={cn("border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-indigo-400 transition-colors", className)}>
        <Upload className="w-6 h-6 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label || 'Upload Photo'}</span>
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => onImageSelected(reader.result);
          reader.readAsDataURL(file);
        }
        e.target.value = '';
      }} />
    </>
  );
};

// ===== HELPER: Step Indicator =====
const StepIndicator = ({ steps, currentStep }) => (
  <div className="flex items-center justify-between mb-8">
    {steps.map((step, index) => {
      const Icon = step.icon;
      const isActive = step.id === currentStep;
      const isComplete = step.id < currentStep;
      return (
        <div key={step.id} className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={cn("w-10 h-10 rounded-full flex items-center justify-center transition-all",
              isComplete ? "bg-green-500 text-white" : isActive ? "bg-indigo-600 text-white" : "bg-gray-200 dark:bg-gray-700 text-gray-500"
            )}>
              {isComplete ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
            </div>
            <span className={cn("text-xs mt-1 hidden md:block", isActive ? "text-indigo-600 font-semibold" : "text-muted-foreground")}>{step.title}</span>
          </div>
          {index < steps.length - 1 && <div className={cn("w-12 lg:w-24 h-1 mx-2", isComplete ? "bg-green-500" : "bg-gray-200 dark:bg-gray-700")} />}
        </div>
      );
    })}
  </div>
);

// ===== HELPER: Profile Completeness =====
const getCompleteness = (formData) => {
  let score = 0;
  let total = 10;
  if (formData.business_name) score++;
  if (formData.business_description?.length >= DESC_GUIDELINES.min) score++;
  if (formData.venue_type) score++;
  if (formData.neighborhood) score++;
  if (formData.address && formData.zip_code) score++;
  if (formData.phone && formData.email) score++;
  if (formData.business_hours.some(h => !h.is_closed)) score++;
  if (formData.amenities.length >= 3) score++;
  if (formData.photos.length >= 2) score++;
  if (formData.logo) score++;
  return Math.round((score / total) * 100);
};

// ===== MAIN COMPONENT =====
const PartnerOnboarding = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  
  // Crop dialog
  const [cropImage, setCropImage] = useState(null);
  const [cropAspect, setCropAspect] = useState(16 / 9);
  const [cropTarget, setCropTarget] = useState(null); // 'photo' | 'logo'
  const [cropTitle, setCropTitle] = useState('');
  
  // Custom amenity
  const [customAmenity, setCustomAmenity] = useState('');
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [generatingExpect, setGeneratingExpect] = useState(false);
  const [generatingExp, setGeneratingExp] = useState(false);

  const [formData, setFormData] = useState({
    business_name: '',
    business_description: '',
    what_to_expect: '',
    venue_type: '',
    price_level: '$$',
    capacity: 50,
    neighborhood: '',
    custom_neighborhood: '',
    address: '',
    city: 'Denver',
    state: 'CO',
    zip_code: '',
    phone: '',
    email: '',
    website: '',
    business_hours: DAYS.map(day => ({ day: day.toLowerCase(), open_time: '11:00', close_time: '23:00', is_closed: false })),
    amenities: [],
    photos: [],
    logo: '',
    menu_items: [],
    menu_url: '',
    accepts_reservations: false,
    reservation_settings: { card_required: true, no_show_fee: 25, cancellation_hours: 24, advance_booking_days: 30 },
    experiences: []
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/auth'); return; }
    checkExistingApplication();
  }, [isAuthenticated, authLoading]);

  const checkExistingApplication = async () => {
    try {
      const response = await api.get('/onboarding/application-status');
      if (response.data.application) {
        setExistingApplication(response.data);
        if (response.data.status === 'approved') navigate('/partner/dashboard');
      }
    } catch (error) { /* no existing application */ }
  };

  const updateFormData = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const generateDescription = async () => {
    if (!formData.business_name) { toast.error('Enter a business name first'); return; }
    setGeneratingDesc(true);
    try {
      const neighborhoodName = NEIGHBORHOODS.find(n => n.id === formData.neighborhood)?.name || formData.custom_neighborhood || 'Denver';
      const res = await api.post('/onboarding/generate-description', {
        business_name: formData.business_name,
        venue_type: formData.venue_type,
        neighborhood: neighborhoodName,
        website: formData.website,
        existing_description: formData.business_description,
        what_to_expect: formData.what_to_expect,
      });
      updateFormData('business_description', res.data.description);
      toast.success('Description generated! Review and customize it.');
    } catch (e) {
      toast.error('Failed to generate description');
    } finally { setGeneratingDesc(false); }
  };

  const generateWhatToExpect = async () => {
    if (!formData.business_name) { toast.error('Enter a business name first'); return; }
    setGeneratingExpect(true);
    try {
      const neighborhoodName = NEIGHBORHOODS.find(n => n.id === formData.neighborhood)?.name || formData.custom_neighborhood || 'Denver';
      const res = await api.post('/onboarding/generate-description', {
        business_name: formData.business_name,
        venue_type: formData.venue_type,
        neighborhood: neighborhoodName,
        website: formData.website,
        existing_description: formData.business_description,
        what_to_expect: 'GENERATE_WHAT_TO_EXPECT',
      });
      updateFormData('what_to_expect', res.data.description);
      toast.success('"What to Expect" generated! Review and customize it.');
    } catch (e) {
      toast.error('Failed to generate');
    } finally { setGeneratingExpect(false); }
  };

  const generateExperienceContent = async (expName) => {
    if (!expName) return null;
    setGeneratingExp(true);
    try {
      const res = await api.post('/onboarding/generate-experience', {
        experience_name: expName, venue_name: formData.business_name, venue_type: formData.venue_type,
      });
      return res.data;
    } catch { toast.error('Failed to generate'); return null; }
    finally { setGeneratingExp(false); }
  };
  
  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity) ? prev.amenities.filter(a => a !== amenity) : [...prev.amenities, amenity]
    }));
  };

  const addCustomAmenity = () => {
    const trimmed = customAmenity.trim();
    if (trimmed && !formData.amenities.includes(trimmed)) {
      setFormData(prev => ({ ...prev, amenities: [...prev.amenities, trimmed] }));
      setCustomAmenity('');
    }
  };

  const updateHours = (dayIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      business_hours: prev.business_hours.map((h, i) => i === dayIndex ? { ...h, [field]: value } : h)
    }));
  };

  // Batch apply hours
  const applyHoursPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      business_hours: prev.business_hours.map(h => h.is_closed ? h : { ...h, open_time: preset.open, close_time: preset.close })
    }));
    toast.success(`Applied "${preset.label}" to all open days`);
  };

  const applyHoursToWeekdays = () => {
    const mondayHours = formData.business_hours[0];
    setFormData(prev => ({
      ...prev,
      business_hours: prev.business_hours.map((h, i) => i < 5 ? { ...h, open_time: mondayHours.open_time, close_time: mondayHours.close_time, is_closed: mondayHours.is_closed } : h)
    }));
    toast.success('Monday hours applied to Mon-Fri');
  };

  const applyHoursToAll = () => {
    const mondayHours = formData.business_hours[0];
    setFormData(prev => ({
      ...prev,
      business_hours: prev.business_hours.map(h => ({ ...h, open_time: mondayHours.open_time, close_time: mondayHours.close_time, is_closed: mondayHours.is_closed }))
    }));
    toast.success('Monday hours applied to all days');
  };

  // Photo handling with crop
  const handlePhotoSelected = (dataUrl) => {
    setCropImage(dataUrl);
    setCropAspect(16 / 9);
    setCropTarget('photo');
    setCropTitle('Crop Venue Photo (16:9 landscape)');
  };

  const handleLogoSelected = (dataUrl) => {
    setCropImage(dataUrl);
    setCropAspect(1);
    setCropTarget('logo');
    setCropTitle('Crop Logo (square)');
  };

  const handleCropComplete = (url) => {
    if (cropTarget === 'photo') {
      setFormData(prev => ({ ...prev, photos: [...prev.photos, url] }));
    } else if (cropTarget === 'logo') {
      setFormData(prev => ({ ...prev, logo: url }));
    }
    setCropImage(null);
    setCropTarget(null);
  };

  const removePhoto = (index) => setFormData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));

  // Menu file upload
  const menuFileRef = useRef(null);
  const handleMenuUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    try {
      const res = await api.post('/upload/file', formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateFormData('menu_url', res.data.url);
      toast.success('Menu uploaded!');
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.business_name || !formData.venue_type) { toast.error('Business name and venue type are required'); return false; }
        if (!formData.business_description || formData.business_description.length < DESC_GUIDELINES.min) { toast.error(`Description must be at least ${DESC_GUIDELINES.min} characters`); return false; }
        break;
      case 2:
        if (!formData.neighborhood) { toast.error('Please select a neighborhood'); return false; }
        if (formData.neighborhood === 'other' && !formData.custom_neighborhood.trim()) { toast.error('Please enter your neighborhood name'); return false; }
        break;
      case 3:
        if (!formData.address || !formData.zip_code || !formData.phone || !formData.email) { toast.error('Please fill in all required fields'); return false; }
        break;
      case 5:
        if (formData.photos.length < 2) { toast.error('Please upload at least 2 venue photos'); return false; }
        break;
    }
    return true;
  };

  const nextStep = () => { if (validateStep()) setCurrentStep(prev => Math.min(prev + 1, 9)); };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const submitApplication = async () => {
    setLoading(true);
    try {
      await api.post('/onboarding/apply', formData);
      toast.success('Application submitted successfully!');
      navigate('/partner/application-status');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally { setLoading(false); }
  };

  if (authLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (!isAuthenticated) return null;

  if (existingApplication && existingApplication.status !== 'rejected') {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className={cn("w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center", existingApplication.status === 'approved' ? "bg-green-100" : "bg-yellow-100")}>
            {existingApplication.status === 'approved' ? <Check className="w-10 h-10 text-green-600" /> : <Clock className="w-10 h-10 text-yellow-600" />}
          </div>
          <h1 className="text-3xl font-bold mb-4">{existingApplication.status === 'approved' ? 'Application Approved!' : 'Application Under Review'}</h1>
          <p className="text-muted-foreground mb-8">{existingApplication.status === 'approved' ? 'Congratulations!' : 'Our team is reviewing your application.'}</p>
          <Button onClick={() => navigate('/partner/application-status')}>View Application Status</Button>
        </div>
      </div>
    );
  }

  const completeness = getCompleteness(formData);
  const descLen = formData.business_description?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-indigo-100 text-indigo-700">Partner Program</Badge>
          <h1 className="text-3xl font-bold text-foreground mb-2">Become an EVENT★GOERS Partner</h1>
          <p className="text-muted-foreground">Build your venue profile and reach thousands of Denver eventgoers</p>
        </div>

        {/* Completeness Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-muted-foreground">Profile Completeness</span>
            <span className={cn("text-sm font-bold", completeness >= 80 ? "text-green-600" : completeness >= 50 ? "text-amber-600" : "text-red-500")}>{completeness}%</span>
          </div>
          <Progress value={completeness} className="h-2" />
        </div>

        <StepIndicator steps={STEPS} currentStep={currentStep} />

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            {/* ===== STEP 1: Business Info ===== */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Tell us about your business</h2>
                  <p className="text-muted-foreground text-sm">This is the foundation of your EVENT★GOERS profile — make it count!</p>
                </div>

                {/* Google Place Import */}
                <div className="p-4 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                  <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4" /> Quick Start: Import from Google</p>
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-3">Search for your business to auto-fill name, address, phone, and website</p>
                  <GooglePlaceSearch onPlaceSelected={async (data) => {
                    // Download Google photos to persist them
                    let persistedPhotos = [];
                    if (data.photos?.length > 0) {
                      toast.info(`Downloading ${data.photos.length} photos...`);
                      for (const photoUrl of data.photos) {
                        try {
                          const res = await api.post('/upload/from-url', { url: photoUrl });
                          if (res.data?.url) { persistedPhotos.push(res.data.url); continue; }
                        } catch {}
                        try {
                          const resp = await fetch(photoUrl, { mode: 'cors' });
                          if (resp.ok) { const blob = await resp.blob(); const fd = new FormData(); fd.append('file', blob, 'photo.jpg'); const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); if (res.data?.url) persistedPhotos.push(res.data.url); }
                        } catch {}
                      }
                    }
                    setFormData(prev => ({
                      ...prev,
                      business_name: data.business_name || prev.business_name,
                      address: data.address || prev.address,
                      city: data.city || prev.city,
                      state: data.state || prev.state,
                      zip_code: data.zip_code || prev.zip_code,
                      phone: data.phone || prev.phone,
                      website: data.website || prev.website,
                      venue_type: data.venue_type || prev.venue_type,
                      neighborhood: data.neighborhood || prev.neighborhood,
                      business_description: data.description || prev.business_description,
                      business_hours: data.business_hours || prev.business_hours,
                      photos: persistedPhotos.length > 0 ? persistedPhotos : prev.photos,
                    }));
                  }} />
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input id="business_name" placeholder="e.g., The Loft RiNo" value={formData.business_name} onChange={(e) => updateFormData('business_name', e.target.value)} data-testid="business-name-input" />
                  </div>

                  <div>
                    <Label htmlFor="venue_type">Venue Type *</Label>
                    <Select value={formData.venue_type} onValueChange={(v) => updateFormData('venue_type', v)}>
                      <SelectTrigger data-testid="venue-type-select"><SelectValue placeholder="Select venue type" /></SelectTrigger>
                      <SelectContent>{VENUE_TYPES.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price Level</Label>
                      <Select value={formData.price_level} onValueChange={(v) => updateFormData('price_level', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="$">$ - Budget</SelectItem>
                          <SelectItem value="$$">$$ - Moderate</SelectItem>
                          <SelectItem value="$$$">$$$ - Upscale</SelectItem>
                          <SelectItem value="$$$$">$$$$ - Fine Dining</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Capacity</Label>
                      <Input type="number" value={formData.capacity} onChange={(e) => updateFormData('capacity', parseInt(e.target.value) || 50)} />
                    </div>
                  </div>

                  {/* Description with guidance */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="business_description">Business Description *</Label>
                      <span className={cn("text-xs font-medium", descLen >= DESC_GUIDELINES.min ? "text-green-600" : "text-red-500")}>{descLen}/{DESC_GUIDELINES.ideal} chars</span>
                    </div>
                    <Textarea id="business_description" rows={5} value={formData.business_description} onChange={(e) => updateFormData('business_description', e.target.value)} data-testid="business-description-input"
                      placeholder="Start with what makes your venue unique. Describe the atmosphere, signature offerings, and why eventgoers should visit..." />
                    <div className="flex items-center justify-between mt-2">
                      <Progress value={Math.min((descLen / DESC_GUIDELINES.ideal) * 100, 100)} className="h-1 flex-1 mr-3" />
                      <Button type="button" variant="outline" size="sm" onClick={generateDescription} disabled={generatingDesc || !formData.business_name} data-testid="generate-desc-btn" className="shrink-0">
                        {generatingDesc ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5 text-indigo-600" /> Generate with AI</>}
                      </Button>
                    </div>
                    
                    {/* Writing tips */}
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1 mb-2"><Lightbulb className="w-3.5 h-3.5" /> Profile Writing Tips</p>
                      <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                        {DESC_GUIDELINES.tips.map((tip, i) => <li key={i}>• {tip}</li>)}
                      </ul>
                      <p className="text-xs text-amber-600 mt-2">Aim for {DESC_GUIDELINES.ideal}+ characters. Min: {DESC_GUIDELINES.min}</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="what_to_expect">What to Expect (recommended)</Label>
                    <Textarea id="what_to_expect" rows={3} value={formData.what_to_expect} onChange={(e) => updateFormData('what_to_expect', e.target.value)}
                      placeholder="What can guests expect? Signature cocktails, live jazz on Fridays, craft beer flights, rooftop views..." />
                    <div className="flex justify-end mt-2">
                      <Button type="button" variant="outline" size="sm" onClick={generateWhatToExpect} disabled={generatingExpect || !formData.business_name} data-testid="generate-expect-btn">
                        {generatingExpect ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5 text-indigo-600" /> Generate with AI</>}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== STEP 2: Neighborhood ===== */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Select Your Neighborhood</h2>
                  <p className="text-muted-foreground text-sm">Your venue will appear in neighborhood-specific searches and recommendations</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {NEIGHBORHOODS.map(n => (
                    <div key={n.id} onClick={() => updateFormData('neighborhood', n.id)}
                      className={cn("p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md",
                        formData.neighborhood === n.id ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"
                      )} data-testid={`neighborhood-${n.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={cn("w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center",
                          formData.neighborhood === n.id ? "border-indigo-600 bg-indigo-600" : "border-gray-300"
                        )}>{formData.neighborhood === n.id && <Check className="w-3 h-3 text-white" />}</div>
                        <div>
                          <h3 className="font-semibold text-foreground">{n.name}</h3>
                          <p className="text-sm text-muted-foreground">{n.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {formData.neighborhood === 'other' && (
                  <div className="mt-4">
                    <Label>Enter Your Neighborhood Name *</Label>
                    <Input placeholder="e.g., Wash Park, Five Points..." value={formData.custom_neighborhood} onChange={(e) => updateFormData('custom_neighborhood', e.target.value)} className="mt-2" data-testid="custom-neighborhood-input" />
                  </div>
                )}
              </div>
            )}

            {/* ===== STEP 3: Location & Contact ===== */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Location & Contact</h2>
                  <p className="text-muted-foreground text-sm">How can customers find and reach you?</p>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Street Address *</Label>
                    <Input placeholder="2920 Larimer St" value={formData.address} onChange={(e) => updateFormData('address', e.target.value)} data-testid="address-input" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>City</Label><Input value={formData.city} onChange={(e) => updateFormData('city', e.target.value)} /></div>
                    <div><Label>State</Label><Input value={formData.state} onChange={(e) => updateFormData('state', e.target.value)} /></div>
                    <div><Label>ZIP Code *</Label><Input placeholder="80205" value={formData.zip_code} onChange={(e) => updateFormData('zip_code', e.target.value)} data-testid="zip-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Phone Number *</Label><Input type="tel" placeholder="(303) 555-0123" value={formData.phone} onChange={(e) => updateFormData('phone', e.target.value)} data-testid="phone-input" /></div>
                    <div><Label>Business Email *</Label><Input type="email" placeholder="info@yourvenue.com" value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} data-testid="email-input" /></div>
                  </div>
                  <div><Label>Website (optional)</Label><Input type="url" placeholder="https://yourvenue.com" value={formData.website} onChange={(e) => updateFormData('website', e.target.value)} /></div>
                </div>
              </div>
            )}

            {/* ===== STEP 4: Hours & Details ===== */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Hours & Amenities</h2>
                  <p className="text-muted-foreground text-sm">Set your hours quickly with presets, then customize</p>
                </div>

                {/* Quick Presets */}
                <div>
                  <Label className="mb-2 block">Quick Presets</Label>
                  <div className="flex flex-wrap gap-2">
                    {HOUR_PRESETS.map(preset => (
                      <Button key={preset.label} variant="outline" size="sm" onClick={() => applyHoursPreset(preset)} data-testid={`preset-${preset.label.split(' ')[0].toLowerCase()}`}>
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Business Hours */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Business Hours</Label>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={applyHoursToWeekdays} data-testid="apply-weekdays-btn">Apply Mon → Mon-Fri</Button>
                      <Button variant="ghost" size="sm" onClick={applyHoursToAll} data-testid="apply-all-btn">Apply Mon → All</Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {formData.business_hours.map((hours, index) => (
                      <div key={hours.day} className="flex items-center gap-3">
                        <div className="w-24"><span className="text-sm font-medium capitalize">{hours.day}</span></div>
                        <Checkbox checked={!hours.is_closed} onCheckedChange={(checked) => updateHours(index, 'is_closed', !checked)} />
                        <span className="text-sm text-muted-foreground w-12">{hours.is_closed ? 'Closed' : 'Open'}</span>
                        {!hours.is_closed && (
                          <>
                            <Input type="time" value={hours.open_time} onChange={(e) => updateHours(index, 'open_time', e.target.value)} className="w-28" />
                            <span>to</span>
                            <Input type="time" value={hours.close_time} onChange={(e) => updateHours(index, 'close_time', e.target.value)} className="w-28" />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Amenities with custom add */}
                <div>
                  <Label className="mb-3 block">Amenities & Features (select all that apply)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[...new Set([...DEFAULT_AMENITIES, ...formData.amenities])].map(amenity => (
                      <div key={amenity} onClick={() => toggleAmenity(amenity)}
                        className={cn("p-2 rounded-lg border cursor-pointer transition-all text-sm",
                          formData.amenities.includes(amenity) ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950" : "border-gray-200 dark:border-gray-700 hover:border-indigo-300"
                        )}>{amenity}</div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Input placeholder="Add custom amenity..." value={customAmenity} onChange={(e) => setCustomAmenity(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomAmenity(); } }} data-testid="custom-amenity-input" />
                    <Button variant="outline" onClick={addCustomAmenity} disabled={!customAmenity.trim()} data-testid="add-amenity-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
                  </div>
                </div>
              </div>
            )}

            {/* ===== STEP 5: Photos & Logo ===== */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Photos & Logo</h2>
                  <p className="text-muted-foreground text-sm">High-quality photos are the #1 factor in driving bookings. Upload at least 2.</p>
                </div>

                {/* Best practices */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 flex items-center gap-1 mb-2"><Info className="w-3.5 h-3.5" /> Photo Best Practices</p>
                  <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <li>• Include a wide shot of the full space (first photo = hero image)</li>
                    <li>• Show unique details: signature drinks, decor, lighting</li>
                    <li>• Use natural/warm lighting — avoid flash</li>
                    <li>• Include photos of events or crowds (social proof)</li>
                    <li>• 4-8 photos is ideal for a complete profile</li>
                  </ul>
                </div>

                {/* Venue Photos */}
                <div>
                  <Label className="mb-3 block">Venue Photos * (min 2, recommended 4-8)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.photos.map((photo, index) => (
                      <div key={index} className="relative group aspect-video rounded-lg overflow-hidden">
                        <img src={photo} alt={`Venue ${index + 1}`} className="w-full h-full object-cover" />
                        {index === 0 && <Badge className="absolute top-2 left-2 bg-indigo-600 text-white text-[10px]">HERO</Badge>}
                        <button onClick={() => removePhoto(index)} className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <PhotoUploadButton onImageSelected={handlePhotoSelected} label="Upload & Crop" className="aspect-video" />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                      const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*';
                      input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file);
                        try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setFormData(prev => ({...prev, photos: [...prev.photos, res.data.url]})); toast.success('Photo uploaded'); } catch { toast.error('Upload failed'); }
                      }; input.click();
                    }}><Upload className="w-3 h-3 mr-1" /> Quick Upload (no crop)</Button>
                    <Input placeholder="Or paste image URL and press Enter" className="h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { setFormData(prev => ({...prev, photos: [...prev.photos, e.target.value.trim()]})); e.target.value = ''; toast.success('Photo added'); }}} />
                  </div>
                  {formData.photos.length < 2 && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> At least 2 photos required ({formData.photos.length}/2)</p>
                  )}
                </div>

                {/* Logo */}
                <div>
                  <Label className="mb-3 block">Logo (recommended)</Label>
                  <div className="flex items-center gap-4">
                    {formData.logo ? (
                      <div className="relative group">
                        <img src={formData.logo} alt="Logo" className="w-24 h-24 object-cover rounded-lg border" />
                        <button onClick={() => updateFormData('logo', '')} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <PhotoUploadButton onImageSelected={handleLogoSelected} label="Upload Logo" className="w-24 h-24" />
                    )}
                    <div className="text-sm text-muted-foreground">
                      <p>Square format recommended (1:1)</p>
                      <p>Your logo appears in search results and listings</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ===== STEP 6: Menu ===== */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Menu (Optional)</h2>
                  <p className="text-muted-foreground text-sm">Upload your menu as a PDF or image, or add menu items</p>
                </div>
                <div>
                  <Label className="mb-2 block">Upload Menu File (PDF or Image)</Label>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" onClick={() => menuFileRef.current?.click()}><Upload className="w-4 h-4 mr-2" /> Choose File</Button>
                    <input ref={menuFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={handleMenuUpload} />
                    {formData.menu_url && (
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="flex items-center gap-1"><FileText className="w-3 h-3" /> Menu uploaded</Badge>
                        <a href={formData.menu_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">View</a>
                        <Button variant="ghost" size="sm" onClick={() => updateFormData('menu_url', '')}><X className="w-4 h-4" /></Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Accepted: PDF, JPG, PNG</p>
                </div>

                {/* Menu Items */}
                <div>
                  <Label className="mb-2 block">Menu Items (add individual items)</Label>
                  {(formData.menu_items || []).map((item, i) => (
                    <div key={i} className="flex gap-2 items-center mb-2">
                      <Input value={item.name || ''} placeholder="Item name" className="flex-1" onChange={e => { const items = [...(formData.menu_items||[])]; items[i] = {...items[i], name: e.target.value}; updateFormData('menu_items', items); }} />
                      <Input value={item.price || ''} placeholder="$" className="w-20" onChange={e => { const items = [...(formData.menu_items||[])]; items[i] = {...items[i], price: e.target.value}; updateFormData('menu_items', items); }} />
                      <Input value={item.category || ''} placeholder="Category" className="w-28" onChange={e => { const items = [...(formData.menu_items||[])]; items[i] = {...items[i], category: e.target.value}; updateFormData('menu_items', items); }} />
                      <Button variant="ghost" size="sm" onClick={() => updateFormData('menu_items', (formData.menu_items||[]).filter((_,idx) => idx !== i))}><X className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => updateFormData('menu_items', [...(formData.menu_items||[]), {name:'', price:'', category:''}])}><Plus className="w-3 h-3 mr-1" /> Add Menu Item</Button>
                </div>
              </div>
            )}

            {/* ===== STEP 7: Reservations ===== */}
            {currentStep === 7 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Reservation Settings</h2>
                  <p className="text-muted-foreground text-sm">Configure if and how customers can book tables through EVENT★GOERS</p>
                </div>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div><p className="font-medium">Accept Reservations</p><p className="text-xs text-muted-foreground">Allow customers to book tables through your profile</p></div>
                  <Switch checked={formData.accepts_reservations} onCheckedChange={v => updateFormData('accepts_reservations', v)} />
                </div>
                {formData.accepts_reservations && <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm font-medium">Require Card Hold</p><p className="text-xs text-muted-foreground">Hold customer's card to prevent no-shows</p></div>
                    <Switch checked={formData.reservation_settings?.card_required} onCheckedChange={v => updateFormData('reservation_settings', {...formData.reservation_settings, card_required: v})} />
                  </div>
                  {formData.reservation_settings?.card_required && <div className="space-y-2">
                    <Label>No-Show Fee ($ per person)</Label>
                    <Input type="number" value={formData.reservation_settings?.no_show_fee || 25} onChange={e => updateFormData('reservation_settings', {...formData.reservation_settings, no_show_fee: parseFloat(e.target.value) || 0})} />
                  </div>}
                  <div className="space-y-2">
                    <Label>Cancellation Window</Label>
                    <Select value={String(formData.reservation_settings?.cancellation_hours || 24)} onValueChange={v => updateFormData('reservation_settings', {...formData.reservation_settings, cancellation_hours: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="2">2 hours</SelectItem><SelectItem value="6">6 hours</SelectItem><SelectItem value="12">12 hours</SelectItem><SelectItem value="24">24 hours</SelectItem><SelectItem value="48">48 hours</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Advance Booking (days ahead)</Label>
                    <Select value={String(formData.reservation_settings?.advance_booking_days || 30)} onValueChange={v => updateFormData('reservation_settings', {...formData.reservation_settings, advance_booking_days: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem><SelectItem value="60">60 days</SelectItem><SelectItem value="90">90 days</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>}
              </div>
            )}

            {/* ===== STEP 8: Experiences ===== */}
            {currentStep === 8 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Create Your Experiences</h2>
                  <p className="text-muted-foreground text-sm">Build experiences with The Hook, The Experience, and The Vibe. AI generates everything from just a name.</p>
                </div>
                {(formData.experiences || []).map((exp, idx) => (
                  <Card key={idx} className="p-4 space-y-3 relative">
                    <button onClick={() => updateFormData('experiences', formData.experiences.filter((_,i) => i !== idx))} className="absolute top-3 right-3 text-red-500"><Trash2 className="w-4 h-4" /></button>
                    
                    <div className="space-y-1.5"><Label>Experience Name *</Label><Input value={exp.title} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], title: e.target.value}; updateFormData('experiences', exps); }} placeholder="e.g., Slow Pour & Secrets Cask Tour" /></div>
                    <div className="space-y-1.5"><Label>Booking Type</Label>
                      <Select value={exp.booking_type || 'open_redemption'} onValueChange={v => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], booking_type: v}; updateFormData('experiences', exps); }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open_redemption">Voucher — Buy now, redeem later</SelectItem>
                          <SelectItem value="scheduled">Scheduled — Customer picks date/time at purchase</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex justify-end"><Button variant="outline" size="sm" disabled={generatingExp || !exp.title} onClick={async () => {
                      const result = await generateExperienceContent(exp.title);
                      if (result) { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], hook: result.hook || '', experience_description: result.experience_description || '', vibe_tags: result.vibe_tags || [], price: result.price_suggestion || 49, duration: result.duration || '90 minutes', category: result.category || 'entertainment', whats_included: result.what_includes || []}; updateFormData('experiences', exps); toast.success('Experience generated!'); }
                    }}>{generatingExp ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1 text-indigo-600" /> Generate with AI</>}</Button></div>
                    
                    <div className="space-y-1.5"><Label className="text-indigo-600 font-semibold">The Hook</Label><Input value={exp.hook || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], hook: e.target.value}; updateFormData('experiences', exps); }} placeholder="One compelling sentence — the elevator pitch" /></div>
                    
                    <div className="space-y-1.5"><Label className="text-indigo-600 font-semibold">The Experience</Label><Textarea value={exp.experience_description || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], experience_description: e.target.value}; updateFormData('experiences', exps); }} rows={3} placeholder="Full description of what happens..." /></div>
                    
                    <div className="space-y-1.5">
                      <Label className="text-indigo-600 font-semibold">The Vibe</Label>
                      <div className="flex flex-wrap gap-2">
                        {[...new Set(['Educational','Exclusive','Behind-the-scenes','Romantic','Social','Adventurous','Relaxing','Creative','Hands-on','VIP','Foodie','Nightlife','Cultural','Outdoors', ...(exp.vibe_tags||[])])].map(tag => (
                          <Badge key={tag} variant={(exp.vibe_tags||[]).includes(tag) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => {
                            const exps = [...formData.experiences]; const current = exps[idx].vibe_tags || [];
                            exps[idx] = {...exps[idx], vibe_tags: current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]};
                            updateFormData('experiences', exps);
                          }}>{tag}</Badge>
                        ))}
                      </div>
                      <Input placeholder="Add custom vibe tag (Enter)" className="h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], vibe_tags: [...(exps[idx].vibe_tags||[]), e.target.value.trim()]}; updateFormData('experiences', exps); e.target.value = ''; }}} />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1.5"><Label>Price ($)</Label><Input type="number" value={exp.price || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], price: parseFloat(e.target.value) || 0}; updateFormData('experiences', exps); }} /></div>
                      <div className="space-y-1.5"><Label>Duration</Label><Input value={exp.duration || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], duration: e.target.value}; updateFormData('experiences', exps); }} placeholder="90 minutes" /></div>
                      <div className="space-y-1.5"><Label>Category</Label><Select value={exp.category || 'entertainment'} onValueChange={v => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], category: v}; updateFormData('experiences', exps); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="food">Food</SelectItem><SelectItem value="drinks">Drinks</SelectItem><SelectItem value="entertainment">Entertainment</SelectItem><SelectItem value="wellness">Wellness</SelectItem><SelectItem value="art">Art</SelectItem><SelectItem value="music">Music</SelectItem><SelectItem value="adventure">Adventure</SelectItem></SelectContent></Select></div>
                    </div>

                    <div className="flex flex-wrap gap-3 pt-2 border-t">
                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer text-sm"><input type="checkbox" checked={exp.is_date_night || false} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], is_date_night: e.target.checked}; updateFormData('experiences', exps); }} className="accent-indigo-600" /> Date Night</label>
                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer text-sm"><input type="checkbox" checked={exp.is_date_night_exclusive || false} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], is_date_night_exclusive: e.target.checked}; updateFormData('experiences', exps); }} className="accent-pink-600" /> Date Night Exclusive</label>
                      <label className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer text-sm"><input type="checkbox" checked={exp.is_eventgoers_exclusive || false} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], is_eventgoers_exclusive: e.target.checked}; updateFormData('experiences', exps); }} className="accent-indigo-600" /> EVENT★GOERS Exclusive</label>
                    </div>

                    <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                      <div className="space-y-1.5"><Label>Valid Until</Label><Input type="date" value={exp.valid_until || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], valid_until: e.target.value}; updateFormData('experiences', exps); }} /></div>
                      <div className="space-y-1.5"><Label>Qty Available</Label><Input type="number" value={exp.quantity_limit || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], quantity_limit: parseInt(e.target.value) || null}; updateFormData('experiences', exps); }} placeholder="Unlimited" /></div>
                      <div className="space-y-1.5"><Label>Min Tickets</Label><Input type="number" value={exp.min_tickets || ''} onChange={e => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], min_tickets: parseInt(e.target.value) || 1}; updateFormData('experiences', exps); }} placeholder="1" /></div>
                    </div>

                    <div className="space-y-1.5 pt-2 border-t">
                      <Label>Experience Photos</Label>
                      <div className="flex gap-2 flex-wrap">
                        {(exp.images||[]).map((img, imgIdx) => (
                          <div key={imgIdx} className="relative group w-20 h-14 rounded overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], images: exps[idx].images.filter((_,i) => i !== imgIdx)}; updateFormData('experiences', exps); }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input placeholder="Paste image URL (Enter)" className="h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], images: [...(exps[idx].images||[]), e.target.value.trim()]}; updateFormData('experiences', exps); e.target.value = ''; }}} />
                        <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); const exps = [...formData.experiences]; exps[idx] = {...exps[idx], images: [...(exps[idx].images||[]), res.data.url]}; updateFormData('experiences', exps); toast.success('Uploaded'); } catch { toast.error('Failed'); } }; input.click(); }}><Upload className="w-3 h-3 mr-1" /> Upload</Button>
                      </div>
                    </div>

                    <div className="space-y-1.5 pt-2 border-t">
                      <Label>What's Included</Label>
                      {(exp.whats_included||[]).map((item, itemIdx) => (
                        <div key={itemIdx} className="flex gap-2 items-center"><Check className="w-4 h-4 text-green-500 shrink-0" /><Input value={item} className="h-8 text-sm" onChange={e => { const exps = [...formData.experiences]; const items = [...(exps[idx].whats_included||[])]; items[itemIdx] = e.target.value; exps[idx] = {...exps[idx], whats_included: items}; updateFormData('experiences', exps); }} /><button onClick={() => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], whats_included: exps[idx].whats_included.filter((_,i) => i !== itemIdx)}; updateFormData('experiences', exps); }} className="text-red-400"><X className="w-4 h-4" /></button></div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => { const exps = [...formData.experiences]; exps[idx] = {...exps[idx], whats_included: [...(exps[idx].whats_included||[]), '']}; updateFormData('experiences', exps); }}><Plus className="w-3 h-3 mr-1" /> Add item</Button>
                    </div>

                    <div className="space-y-1.5 pt-2 border-t">
                      <Label>Terms & Conditions</Label>
                      {(exp.terms||['Valid for single use only','Non-refundable after purchase','Must be redeemed before expiry date','Subject to availability']).map((term, tIdx) => (
                        <div key={tIdx} className="flex gap-2 items-center"><span className="text-muted-foreground shrink-0">•</span><Input value={term} className="h-8 text-sm" onChange={e => { const exps = [...formData.experiences]; const t = [...(exps[idx].terms||['Valid for single use only','Non-refundable after purchase','Must be redeemed before expiry date','Subject to availability'])]; t[tIdx] = e.target.value; exps[idx] = {...exps[idx], terms: t}; updateFormData('experiences', exps); }} /><button onClick={() => { const exps = [...formData.experiences]; const t = [...(exps[idx].terms||[])]; t.splice(tIdx,1); exps[idx] = {...exps[idx], terms: t}; updateFormData('experiences', exps); }} className="text-red-400"><X className="w-4 h-4" /></button></div>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => { const exps = [...formData.experiences]; const t = [...(exps[idx].terms||[])]; t.push(''); exps[idx] = {...exps[idx], terms: t}; updateFormData('experiences', exps); }}><Plus className="w-3 h-3 mr-1" /> Add term</Button>
                    </div>
                  </Card>
                ))}
                <Button variant="outline" className="w-full" onClick={() => updateFormData('experiences', [...(formData.experiences || []), { title: '', hook: '', experience_description: '', vibe_tags: [], price: 0, duration: '', category: 'entertainment', whats_included: [], terms: [], images: [] }])}><Plus className="w-4 h-4 mr-2" /> Add Experience</Button>
                <p className="text-xs text-muted-foreground text-center">Optional — experiences can also be added later from your partner dashboard.</p>
              </div>
            )}

            {/* ===== STEP 9: Review ===== */}
            {currentStep === 9 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold mb-1">Review Your Profile</h2>
                  <p className="text-muted-foreground text-sm">Make sure everything looks great before submitting</p>
                </div>

                {/* Completeness */}
                <div className={cn("p-4 rounded-lg border-2", completeness >= 80 ? "border-green-300 bg-green-50 dark:bg-green-950/20" : "border-amber-300 bg-amber-50 dark:bg-amber-950/20")}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold">Profile Completeness</span>
                    <span className={cn("text-2xl font-bold", completeness >= 80 ? "text-green-600" : "text-amber-600")}>{completeness}%</span>
                  </div>
                  <Progress value={completeness} className="h-2" />
                  {completeness < 100 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {formData.photos.length < 2 && '• Add more photos. '}
                      {!formData.logo && '• Add a logo. '}
                      {formData.amenities.length < 3 && '• Select more amenities. '}
                      {descLen < DESC_GUIDELINES.min && '• Expand your description. '}
                    </p>
                  )}
                </div>

                {/* Summary */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-start gap-4">
                    {formData.logo && <img src={formData.logo} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                    <div>
                      <h3 className="font-bold text-lg">{formData.business_name}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <Badge>{formData.venue_type}</Badge>
                        <Badge variant="outline">{formData.price_level}</Badge>
                        <Badge variant="outline">{formData.capacity} capacity</Badge>
                        <Badge className="bg-indigo-100 text-indigo-700">{NEIGHBORHOODS.find(n => n.id === formData.neighborhood)?.name || formData.custom_neighborhood}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-3">{formData.business_description}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Address</p><p className="font-medium">{formData.address}, {formData.city}, {formData.state} {formData.zip_code}</p></div>
                  <div><p className="text-muted-foreground">Contact</p><p className="font-medium">{formData.phone}</p><p className="font-medium">{formData.email}</p></div>
                </div>

                <div>
                  <p className="text-muted-foreground text-sm mb-2">Photos ({formData.photos.length})</p>
                  <div className="flex gap-2 overflow-x-auto">{formData.photos.map((photo, i) => <img key={i} src={photo} alt="" className="w-20 h-14 object-cover rounded" />)}</div>
                </div>

                <div>
                  <p className="text-muted-foreground text-sm mb-2">Amenities ({formData.amenities.length})</p>
                  <div className="flex flex-wrap gap-1">{formData.amenities.map(a => <Badge key={a} variant="secondary" className="text-xs">{a}</Badge>)}</div>
                </div>

                {formData.accepts_reservations && (
                  <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                    <p className="text-sm font-medium text-green-800">Reservations: Enabled</p>
                    <p className="text-xs text-green-600">Card hold: {formData.reservation_settings?.card_required ? 'Yes' : 'No'} | Cancellation: {formData.reservation_settings?.cancellation_hours}hr</p>
                  </div>
                )}

                {(formData.experiences||[]).length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-sm mb-2">Experiences ({formData.experiences.length})</p>
                    {formData.experiences.map((exp, i) => (
                      <div key={i} className="p-3 border rounded-lg mb-2">
                        <p className="font-medium text-sm">{exp.title}</p>
                        {exp.hook && <p className="text-xs text-muted-foreground italic">{exp.hook}</p>}
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">${exp.price}</Badge>
                          {exp.is_date_night && <Badge className="bg-pink-100 text-pink-700 text-[10px]">Date Night</Badge>}
                          {exp.is_eventgoers_exclusive && <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Exclusive</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-amber-800 dark:text-amber-200 text-sm"><strong>Commission Structure:</strong></p>
                  <ul className="text-amber-700 dark:text-amber-300 text-sm mt-2 space-y-1">
                    <li>• Event Tickets: 12%</li>
                    <li>• Experiences/Vouchers: 30%</li>
                    <li>• Private Hire Bookings: 15%</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}><ChevronLeft className="w-4 h-4 mr-2" /> Previous</Button>
              {currentStep < 9 ? (
                <Button onClick={nextStep}>Next <ChevronRight className="w-4 h-4 ml-2" /></Button>
              ) : (
                <Button onClick={submitApplication} disabled={loading} className="bg-green-600 hover:bg-green-700">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting...</> : <>Submit Application <Check className="w-4 h-4 ml-2" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Crop Dialog */}
      <ImageCropDialog open={!!cropImage} onClose={() => setCropImage(null)} imageSrc={cropImage} aspectRatio={cropAspect} onCropComplete={handleCropComplete} title={cropTitle} />
    </div>
  );
};

export default PartnerOnboarding;
